import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  CoPubliciteListItemDto,
  CoPubliciteTableSortBy,
  DashboardTableSortDirection,
  MentionBoostDeliveryState,
} from '@fluenceur/types';
import { useQuery, type QueryClient } from '@tanstack/react-query';

const FIVE_MINUTES_MS = 5 * 60 * 1000;
export const CO_PUBLICITES_QUERY_KEY_PREFIX = ['coPublicites'] as const;

interface CoPublicitesQueryParams {
  state: MentionBoostDeliveryState | null;
  sortBy: CoPubliciteTableSortBy;
  sortDirection: DashboardTableSortDirection;
}

/**
 * Shared query-key root for the Co-publicites index. Keyed on brand + delivery
 * state + sort so each view caches independently and a sort change refetches the
 * server-ordered list (sorting is server-side, never a client reshuffle).
 */
export const coPublicitesQueryKey = (brandId: string, params: CoPublicitesQueryParams) =>
  [
    ...CO_PUBLICITES_QUERY_KEY_PREFIX,
    brandId,
    params.state ?? null,
    params.sortBy,
    params.sortDirection,
  ] as const;

/** Refresh every brand/filter/sort variant after a boost lifecycle mutation. */
export const invalidateCoPublicitesQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: CO_PUBLICITES_QUERY_KEY_PREFIX });

const buildListPath = ({ state, sortBy, sortDirection }: CoPublicitesQueryParams): string => {
  const search = new URLSearchParams({ sortBy, sortDirection });
  if (state) {
    search.set('state', state);
  }
  return `/dashboard/collabs/co-publicites?${search.toString()}`;
};

const fetchCoPublicites = async (
  params: CoPublicitesQueryParams
): Promise<CoPubliciteListItemDto[]> => {
  const { data } = await api.get<ApiResponse<CoPubliciteListItemDto[]>>(buildListPath(params));
  return data.data ?? [];
};

interface UseCoPublicitesQueryOptions {
  brandId: string | null | undefined;
  state?: MentionBoostDeliveryState | null;
  sortBy: CoPubliciteTableSortBy;
  sortDirection: DashboardTableSortDirection;
}

/**
 * List the brand's co-publicites (boosted mentions) for the Collabs index. The
 * delivery-state filter and the sort are both applied server-side; the array is
 * the envelope `data`. Disabled until a brand id resolves so we never fire an
 * unscoped request on boot.
 */
export const useCoPublicitesQuery = ({
  brandId,
  state = null,
  sortBy,
  sortDirection,
}: UseCoPublicitesQueryOptions) => {
  const params: CoPublicitesQueryParams = { state, sortBy, sortDirection };

  return useQuery<CoPubliciteListItemDto[], Error>({
    queryKey: coPublicitesQueryKey(brandId ?? '', params),
    queryFn: () => fetchCoPublicites(params),
    enabled: !!brandId,
    staleTime: FIVE_MINUTES_MS,
  });
};

export { fetchCoPublicites };
